import React, { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

interface KanbanColumnProps {
  columnKey: string;
  label: string;
  accentColor: string;
  count: number;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}

export default function KanbanColumn({
  columnKey,
  label,
  accentColor,
  count,
  collapsible,
  defaultCollapsed,
  children,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: columnKey });
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false);

  return (
    <section
      ref={setNodeRef}
      className={`flex flex-col rounded-lg border transition-colors duration-150 min-h-[300px] ${
        isOver
          ? "border-primary-400 bg-primary-50/50 ring-2 ring-primary-300/50"
          : "border-gray-200 bg-gray-50/50"
      }`}
    >
      {/* Column header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 bg-white rounded-t-lg">
        <span
          className="inline-block h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: accentColor }}
        />
        <h3 className="text-sm font-semibold text-gray-900">{label}</h3>
        <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
          {count}
        </span>
        {collapsible && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {collapsed ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronUpIcon className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Cards area */}
      {!collapsed && (
        <div className="flex flex-col gap-2 p-2 flex-1 overflow-y-auto max-h-[calc(100vh-320px)]">
          {children}
        </div>
      )}
      {collapsed && (
        <div className="p-3 text-center text-xs text-gray-400">
          {count} {count === 1 ? "taak" : "taken"}
        </div>
      )}
    </section>
  );
}
